<template>
	<div
		class="add-to-cart-row flex flex-row gap-4 mt-4 flex-wrap xl:flex-nowrap"
	>
		<div v-if="!wishProduct && !hideQuantity">
			<QuantitySelector v-model="quantity" :min="1" :max="stock" />
		</div>
		<button
			:class="[
				ctaClass || 'bg-main-button hover:bg-gray-900',
				'add-to-cart-button lg:max-w-[316px] w-full h-auto py-3 text-white font-medium text-base px-3 transition ease-in-out duration-200 disabled:bg-gray-100 order-last xl:order-none',
				`${selectedSecondParametric?.InStock && 'cursor-pointer'} ${
					stock === 0 && 'text-gray-500 bg-gray-100'
				}`
			]"
			data-testid="add-to-cart-button"
			:disabled="
				cartStore.updating || disableButton || forceDisabled || stock === 0
			"
			@click="handleAddToCart"
		>
			<div v-if="cartStore.updating">
				<div
					class="inline-block h-5 w-5 mr-4 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
					role="status"
				></div>
				<span>{{ $t("Adding") }}..</span>
			</div>
			<span v-else>{{
				stock !== 0 ? $t("Add to Cart") : $t("no_longer_available")
			}}</span>
		</button>
		<div
			v-if="!wishProduct && selectedProduct && !hideWishlist"
			class="add-to-cart-wishlist ml-auto"
		>
			<Favorite
				wrapper-size="[50px]"
				rounded="none"
				:border="{ color: 'gray', weight: 2 }"
				:product="selectedProduct"
				:variant-parametrics-number="
					parametrics && Object.keys(parametrics).length
						? Object.keys(parametrics).length
						: 0
				"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
// `add-to-cart-*` classes are styling hooks (styled in sportkost), not dead code.
import { ref } from "vue";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { MultiPackCartLine } from "~/layers/base/stores/cartStore";
import Favorite from "../../../Favorite/Favorite.vue";
import QuantitySelector from "./QuantitySelector.vue";

interface Props {
	stock: number;
	wishProduct?: ProductItem;
	/** Pack children — `Quantity` scales with the parent quantity. */
	optionalItems?: { PartNo: string; Quantity: number }[];
	/** Extra children added as-is, not scaled by the parent quantity. */
	fixedOptionalItems?: { PartNo: string; Quantity: number }[];
	/**
	 * Multi-pack ("mixflak") items. When set, THESE go in the basket as
	 * independent lines and the product on the page is not added at all —
	 * see `cartStore.addMultiPackToCart`.
	 */
	multiPackItems?: MultiPackCartLine[];
	/**
	 * The picked items as catalogue products plus their amounts, for the
	 * analytics report — `multiPackItems` carries basket lines, which lack the
	 * Id and price GTM needs. Named `picks`, not `products`, to stay clear of
	 * `productStore.multiPackProducts`, which is the whole pool.
	 */
	multiPackPicks?: { product: ProductItem; quantity: number }[];
	forceDisabled?: boolean;
	ctaClass?: string;
	/** Opt-in: render without the quantity selector (quantity stays 1). */
	hideQuantity?: boolean;
	/** Opt-in: render without the wishlist button. */
	hideWishlist?: boolean;
}
const {
	stock,
	wishProduct,
	optionalItems,
	fixedOptionalItems,
	multiPackItems,
	multiPackPicks,
	forceDisabled,
	ctaClass,
	hideQuantity,
	hideWishlist
} = defineProps<Props>();

const cartStore = useCartStore();

const { selectedSecondParametric, selectedProduct, parametrics } =
	storeToRefs(useProductStore());

const disableButton = useAddToCartDisabled(() => stock, {
	wishProduct: () => wishProduct
});

const quantity = ref(1);

// The press itself lives in a composable: this component is the button, and
// the payload for three basket shapes plus the add-ons was most of the file.
const { handleAddToCart } = useAddToCartAction(quantity, () => ({
	wishProduct,
	optionalItems,
	fixedOptionalItems,
	multiPackItems,
	multiPackPicks
}));
</script>
